import { Box } from '@mui/material';
import { useLayoutEffect, useState, type ReactNode } from 'react';

import { PhoneScreenContext } from '@/designs-dev/device/PhoneScreenContext';

// Logical screen size of the device the marketing frame is built around
// (iPhone 12 Mini — see Figma "Product marketing assets" 8:17044).
const SCREEN_W = 375;
const SCREEN_H = 812;
const BEZEL = 14; // Cherry bezel thickness around the screen
const FRAME_W = SCREEN_W + BEZEL * 2; // full bezel footprint
const FRAME_H = SCREEN_H + BEZEL * 2;
// Room reserved below the phone when fitting to height: the floating controls
// pill (about 70px with its offset from the bottom) plus a gap above it, so the
// phone never touches the controls.
const BOTTOM_MARGIN = 96;
const SIDE_GUTTER = 16;

/**
 * Device mockup that wraps a screen in the KareHero brand (Cherry) phone bezel,
 * matching the Product marketing phone frame in Figma. Children render inside the
 * 375×812 screen and scroll independently, so prototype screens "look nice" in a
 * realistic device rather than filling the browser window.
 *
 * Like ComputerFrame, the phone scales itself down to fit the viewport height so
 * the whole device is visible on a laptop without zooming the browser out. The
 * logical screen stays 375×812 (a real phone size); only the rendered frame is
 * scaled. It never scales above 1 (full size).
 *
 * `scale` opts out of that: pass one and the frame renders at exactly that size
 * and stops measuring the window. That is for a fixed-size canvas — the site
 * mockups export at a known width, and a phone that fitted itself to the
 * viewport would make the export depend on how tall the browser happened to be.
 */
// `inspecting` swaps the bezel from Cherry to Sunflower (fading) so the frame
// signals design-system mode alongside the charcoal stage.
const PhoneFrame = ({
  children,
  inspecting = false,
  scale: fixedScale,
}: {
  children?: ReactNode;
  inspecting?: boolean;
  /** Render at this scale instead of fitting to the viewport height. */
  scale?: number;
}) => {
  const [screenEl, setScreenEl] = useState<HTMLElement | null>(null);
  const [wrapEl, setWrapEl] = useState<HTMLElement | null>(null);
  const [fit, setFit] = useState(0.9);

  useLayoutEffect(() => {
    if (!wrapEl || fixedScale) return;
    const compute = () => {
      // Fit below whatever chrome sits above the frame (stage padding, the
      // exit button). Self-referential against the footprint's own top, which
      // converges: each pass halves the error as the wrapper re-centres while
      // it rescales, mirroring ComputerFrame.
      const top = Math.max(
        0,
        wrapEl.getBoundingClientRect().top + window.scrollY,
      );
      const byHeight = (window.innerHeight - top - BOTTOM_MARGIN) / FRAME_H;
      // And to the width, so on a phone the frame shrinks to the screen rather
      // than running off its side. A side gutter of 16px each way.
      const byWidth = (window.innerWidth - SIDE_GUTTER * 2) / FRAME_W;
      setFit(Math.min(1, byHeight, byWidth));
    };
    compute();
    const observer = new ResizeObserver(compute);
    observer.observe(wrapEl);
    window.addEventListener('resize', compute);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', compute);
    };
  }, [wrapEl, fixedScale]);

  const scale = fixedScale ?? Math.max(0.4, fit);

  const frame = inspecting ? 'sunflower.main' : 'cherry.main';
  const button = inspecting ? 'sunflower.dark' : 'cherry.dark';

  return (
    // Reserve only the scaled footprint so the stage doesn't lay out the phone
    // at full ~840px height and overflow on short laptop viewports.
    <Box
      sx={{ width: FRAME_W * scale, height: FRAME_H * scale, flexShrink: 0 }}
      ref={setWrapEl}
    >
      <Box sx={{ transform: `scale(${scale})`, transformOrigin: 'top left' }}>
        <Box
          sx={{
            position: 'relative',
            width: FRAME_W,
            height: FRAME_H,
            bgcolor: frame,
            borderRadius: '56px',
            p: `${BEZEL}px`,
            boxSizing: 'border-box',
            flexShrink: 0,
            transition: 'background-color 400ms ease',
          }}
        >
          {/* side buttons (silent switch + volume on the left, power on the right) */}
          <Box
            sx={{
              position: 'absolute',
              left: -2,
              top: 150,
              width: 3,
              height: 26,
              borderRadius: '2px',
              bgcolor: button,
              transition: 'background-color 400ms ease',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              left: -2,
              top: 196,
              width: 3,
              height: 50,
              borderRadius: '2px',
              bgcolor: button,
              transition: 'background-color 400ms ease',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              left: -2,
              top: 258,
              width: 3,
              height: 50,
              borderRadius: '2px',
              bgcolor: button,
              transition: 'background-color 400ms ease',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              right: -2,
              top: 220,
              width: 3,
              height: 80,
              borderRadius: '2px',
              bgcolor: button,
              transition: 'background-color 400ms ease',
            }}
          />

          {/* screen */}
          <Box
            sx={{
              width: SCREEN_W,
              height: SCREEN_H,
              borderRadius: '42px',
              overflow: 'auto',
              bgcolor: 'background.default',
              position: 'relative',
            }}
            ref={setScreenEl}
            className='phone-screen'
          >
            <PhoneScreenContext.Provider value={screenEl}>
              {children}
            </PhoneScreenContext.Provider>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PhoneFrame;
